/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { MutableRef, useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { DataState, SelectionDetail } from '../UNSAFE_Collection';
import { Item } from '../utils/UNSAFE_dataProvider';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { isControlOrFunctionKey, KEYS, useSelectCommon } from '../PRIVATE_SelectCommon';
import { SelectMultiple } from './SelectMultiple';
import { SelectedValuesCount } from './SelectedValuesCount';
import { useSelectedOnlyData } from './useSelectedOnlyData';

type PickedPropsFromSelectMultiple = Pick<
  ComponentProps<typeof SelectMultiple>,
  'isDisabled' | 'isReadonly' | 'onFilter' | 'onLoadRange'
>;
type UseSelectMultipleProps<K, D> = PickedPropsFromSelectMultiple & {
  data?: DataState<K, D> | null;
  inputRef: MutableRef<HTMLInputElement>;
  isFocused?: boolean;
  onCommit: (detail: ValueUpdateDetail<Set<K>>) => void;
  valueItems?: Item<K, D>[];
};

type SelectedValuesCountToggleType = ComponentProps<typeof SelectedValuesCount>['onToggle'];
type SelectedValuesCountToggleDetailType = Parameters<
  Exclude<SelectedValuesCountToggleType, undefined>
>[0];

/**
 * Hook that manages SelectMultiple state and behavior.  This hook creates state variables and
 * event listeners, returning properties to apply to components internally rendered by
 * SelectMultiple, as well as state information.
 *
 * @param data Specifies data for the dropdown list.
 * @param inputRef Ref to the input element.
 * @param isDisabled Specifies whether the component is disabled.
 * @param isFocused Specifies whether the component has focus.
 * @param isReadonly Specifies whether the component is readonly.
 * @param onCommit Callback invoked when the selected values are committed.
 * @param onFilter Callback function to trigger loading data for the dropdown list,
 * which may or may not be filtered by user entered text.
 * @param onLoadRange Callback function to handle when the viewport of the dropdown list has
 * changed, including the case where the user scrolls to the end of the list and there are more
 * items to load.  The function should set a new DataState on the component for the specified
 * range.
 * @param valueItems Specifies the keys, data, and optional metadata for the selected values.
 *
 * @returns Properties to apply to internal components that SelectMultiple renders, and component
 * state.
 */
export function useSelectMultiple<K, D>({
  data: propData,
  inputRef,
  isDisabled,
  isFocused: propIsFocused,
  isReadonly,
  onCommit,
  onFilter,
  onLoadRange: propOnLoadRange,
  valueItems
}: UseSelectMultipleProps<K, D>) {
  const [isDropdownSelectedOnlyView, setDropdownSelectedOnlyView] = useState(false);

  const selectedValuesCount = valueItems?.length;
  const hasSelectedValuesCount =
    (selectedValuesCount !== undefined && selectedValuesCount > 0) || isDropdownSelectedOnlyView;

  // show only the selected values in the dropdown if isDropdownSelectedOnlyView is true,
  // otherwise show the full data set
  // TODO: We may need to revisit this for hierarchical data because the selected valueItems
  // won't have any information about ancestor group nodes, and we should probably show selected
  // items in context within the hierarchy.  Also, the order of the items in the selected-only
  // view right now follows the order in which they were selected, not the order in which they
  // would appear in the full data set.  This may be more of a problem for hierarchical data
  // if items have to appear within the correct group ancestors.  We may need to move
  // construction of the selected-only DataState out of the component to the consuming code,
  // and take a callback that we can call as a notification that the app should provide this
  // special DataState to us (kind of like onLoadRange).  Note that we may also need an
  // enhancement on the DataProvider API to be able to specify a filterCriterion to filter by
  // the selected keys through a fetchFirst call, because a regular fetchByKeys call would
  // still return results in the order in which the keys were specified and would not return
  // data for the ancestor group nodes.
  const selectedOnlyDataProps = useSelectedOnlyData<K, D>({
    isDropdownSelectedOnlyView,
    valueItems
  });
  const data = isDropdownSelectedOnlyView ? selectedOnlyDataProps.data : propData;
  const onLoadRange = isDropdownSelectedOnlyView
    ? selectedOnlyDataProps.onLoadRange
    : propOnLoadRange;

  const {
    currentRow,
    dropdownRef,
    handleDropdownArrowClick,
    handleDropdownAutoDismiss: baseHandleDropdownAutoDismiss,
    handleInput,
    handleMainFieldKeyDown: baseHandleMainFieldKeyDown,
    handleMainFieldKeyUp,
    handleUpDownArrowKeys,
    isDropdownOpen,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    onCurrentKeyChange,
    onMouseDown,
    searchText,
    selectedKeys,
    setCurrentRow,
    setDropdownOpen,
    setSelectedKeys,
    userInput
  } = useSelectCommon<K, D>({
    data,
    inputRef,
    isFocused: propIsFocused,
    onFilter
  });

  const removeDropdown = useCallback(() => {
    dropdownRef.current?.parentElement?.removeChild(dropdownRef.current);
  }, []);

  const myHandleDropdownAutoDismiss = useCallback(
    (event?: Event) => {
      if (isDropdownOpen) {
        if (event?.type === 'keydown') {
          const keyboardEvent = event as KeyboardEvent;
          switch (keyboardEvent.code) {
            case KEYS.TAB:
              // Put focus on an element in the main field, but don't kill the event, so that
              // the browser can naturally transfer focus to the next focusable item relative
              // to the element in the main field.
              if (keyboardEvent.shiftKey || !hasSelectedValuesCount) {
                inputRef.current?.focus();
              } else {
                chipRef.current?.focus();
              }

              // Remove the dropdown immediately, without waiting for a rerender based on changing
              // state, so that the open dropdown cannot get focus when the browser handles the
              // Tab key.
              removeDropdown();
              break;
            default:
              break;
          }
        }
      }
    },
    [hasSelectedValuesCount, isDropdownOpen]
  );

  const myHandleMainFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // const target = event.target as HTMLElement;
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && (event.shiftKey || (!hasSelectedValuesCount && !event.shiftKey))) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the selected values count chip
          if (hasSelectedValuesCount && !event.shiftKey) {
            event.stopPropagation();
          }
          break;

        default:
          break;
      }
    },
    [hasSelectedValuesCount, isDropdownOpen]
  );

  // when the array of valueItems changes, pull out the new set of selectedKeys
  useEffect(() => {
    const keys = valueItems?.reduce((accumKeys, currentItem) => {
      accumKeys.add(currentItem.key);
      return accumKeys;
    }, new Set<K>());
    setSelectedKeys(keys);
  }, [valueItems]);

  const chipRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    chipRef.current =
      hasSelectedValuesCount && mainFieldRef.current
        ? mainFieldRef.current.querySelector('[role="switch"]')
        : null;
  }, [hasSelectedValuesCount, isDisabled, isReadonly]);

  const handleSelectedValuesCountKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && !event.shiftKey) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the input
          if (event.shiftKey) {
            event.stopPropagation();
          }
          break;

        case KEYS.UP:
        case KEYS.DOWN:
          // prevent the page from scrolling
          event.preventDefault();
          break;

        case KEYS.ESC:
          if (isDropdownOpen) {
            setDropdownOpen(false);

            event.preventDefault();

            // JET-53183 - core pack compat: stop propagation of handled events
            // we don't want a parent collection component to stop editing the row when we close
            // the dropdown
            event.stopPropagation();
          }
          break;

        case KEYS.ENTER:
          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to move focus to the next row when
          // we toggle between the full and selected only list in the dropdown
          event.stopPropagation();
          break;

        default:
          break;
      }
    },
    [isDropdownOpen]
  );

  const handleSelectedValuesCountKeyUp = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          handleUpDownArrowKeys(event);
          break;

        default:
          break;
      }
    },
    [handleUpDownArrowKeys]
  );

  const handleSelectedValuesCountMouseDown = useCallback((event: MouseEvent) => {
    // call preventDefault so that the dropdown doesn't open showing the full list on mousedown
    // on the chip before switching to show only the selected values on mouseup when the click
    // is handled
    event.preventDefault();
    // explicitly focus the input because it won't happen naturally due to preventDefault()
    inputRef.current?.focus();
  }, []);

  const handleSelectedValuesCountToggle = useCallback(
    (detail: SelectedValuesCountToggleDetailType) => {
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      }

      const value = detail.value ?? false;
      if (value !== isDropdownSelectedOnlyView) {
        setDropdownSelectedOnlyView(value);
      }
    },
    [isDropdownOpen, isDropdownSelectedOnlyView]
  );

  // when the dropdown closes, automatically turn off isDropdownSelectedOnlyView
  useEffect(() => {
    if (!isDropdownOpen && isDropdownSelectedOnlyView) {
      setDropdownSelectedOnlyView(false);
    }
  }, [isDropdownOpen, isDropdownSelectedOnlyView]);

  const onSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      const newKeys = new Set(detail.value.keys?.values());
      // update the selectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for new valueItems to be set
      setSelectedKeys(newKeys);
      // call the onCommit callback so that the consuming code can update the valueItems
      onCommit?.({ value: newKeys, previousValue: selectedKeys });
    },
    [onCommit, selectedKeys]
  );

  const dropdownEventHandlers = useMemo(() => {
    return mergeProps(
      { onAutoDismiss: baseHandleDropdownAutoDismiss },
      { onAutoDismiss: myHandleDropdownAutoDismiss }
    );
  }, [baseHandleDropdownAutoDismiss, myHandleDropdownAutoDismiss]);

  const inputEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onInput: handleInput,
        onKeyDown: baseHandleMainFieldKeyDown,
        onKeyUp: handleMainFieldKeyUp
      },
      { onKeyDown: myHandleMainFieldKeyDown }
    );
  }, [baseHandleMainFieldKeyDown, handleInput, handleMainFieldKeyUp, myHandleMainFieldKeyDown]);

  useEffect(() => {
    // clear the current item when the dropdown opens
    if (isDropdownOpen) {
      setCurrentRow({ rowKey: undefined });
    }
  }, [isDropdownOpen]);

  return {
    collectionProps: {
      currentKey: currentRow.rowKey,
      onCurrentKeyChange,
      onSelectionChange,
      selectedKeys
    },
    data,
    dropdownArrowEventHandlers: {
      onClick: handleDropdownArrowClick
    },
    dropdownEventHandlers,
    dropdownRef,
    hasSelectedValuesCount,
    inputEventHandlers,
    inputRef,
    isDropdownOpen,
    isDropdownSelectedOnlyView,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    mouseProps: isDisabled || isReadonly ? {} : { onMouseDown },
    onLoadRange,
    searchText,
    selectedValuesCountProps: {
      onKeyDown: handleSelectedValuesCountKeyDown,
      onKeyUp: handleSelectedValuesCountKeyUp,
      onMouseDown: handleSelectedValuesCountMouseDown,
      onToggle: handleSelectedValuesCountToggle
    },
    userInput
  };
}
